import React, { useState } from 'react';
import {
  Paper,
  CardContent,
  Box,
  Avatar,
  Typography,
  Chip,
  useTheme,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Public, Lock, Group, Delete, Flag, MoreHoriz, Send, CheckCircleOutlineTwoTone } from '@mui/icons-material';
import { Feed, JobDetails, JobSeekerDetails } from '@/constants/types';
import FeedMedia from './FeedMedia';
import { FeedActions } from './FeedActions';
import { ContentWithReadMore } from './ContentWithReadMore';
import { CommentList } from './comments';
import { JobDetailsCard } from './JobDetailsCard';
import { JobSeekerCard } from './JobSeekerCard';
import { useFeedHover } from '@/hooks/useFeedHover';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { followUser, unfollowUser, cancelFollowRequest, deleteFeed, updateFeedStats } from '@/store';
import { USER_ROLES } from '@/constants/app';
import { feedService } from '@/services/feed/feedService';
import toast from 'react-hot-toast';
import { PollCard } from './PollCard';

const REPORT_REASONS = [
  'Harassment',
  'Fraud or scam',
  'Spam',
  'Misinformation',
  'Hateful speech',
  'Threats or violence',
  'Self-harm',
  'Graphic content',
  'Dangerous or extremist organizations',
  'Sexual content',
  'Fake account',
  'Child exploitation',
  'Illegal goods and services',
  'Infringement'
];

interface FeedItemProps {
  feed: Feed;
  expandedContent: Set<string>;
  setExpandedContent: React.Dispatch<React.SetStateAction<Set<string>>>;
  onImageClick: (url: string, alt: string) => void;
  onDocumentClick: (url: string, fileName: string) => void;
  openCommentsFeedId: string | null;
  setOpenCommentsFeedId: React.Dispatch<React.SetStateAction<string | null>>;
  hideActions?: boolean;
}

export const FeedItem: React.FC<FeedItemProps> = ({
  feed,
  expandedContent,
  setExpandedContent,
  onImageClick,
  onDocumentClick,
  openCommentsFeedId,
  setOpenCommentsFeedId,
  hideActions = false,
}) => {
  console.log(expandedContent)
  const theme = useTheme();
  const { handleMouseEnter } = useFeedHover(feed._id);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);
  const showComments = openCommentsFeedId === feed._id;

  const [localIsFollowing, setLocalIsFollowing] = useState(feed.relationshipStatus?.isFollowing || false);
  const [localRequestPending, setLocalRequestPending] = useState(feed.relationshipStatus?.isRequestSent || false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [isReporting, setIsReporting] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  // Don't show buttons if viewing own feed
  const isOwnFeed = currentUser?.id === feed.userId._id;

  // Check if user is admin
  const isAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN || currentUser?.role === USER_ROLES.ADMINISTRATOR;

  const handleCommentToggle = () => {
    if (showComments) {
      // If this feed's comments are open, close them
      setOpenCommentsFeedId(null);
    } else {
      // If this feed's comments are closed, open them (and close any other)
      setOpenCommentsFeedId(feed._id);
    }
  };

  const handleFollow = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (localIsFollowing || feed.relationshipStatus?.isFollowing) {
      // Unfollow user
      setLocalIsFollowing(false);
      await dispatch(unfollowUser(feed.userId._id));
    } else {
      // Follow user
      setLocalRequestPending(true);
      await dispatch(followUser({ recipientId: feed.userId._id }));
    }
  };

  const handleCancelRequest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalRequestPending(false);
    await dispatch(cancelFollowRequest({ recipientId: feed.userId._id }));
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteFeed(feed._id)).unwrap();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete feed:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event?: any) => {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    setReportDialogOpen(true);
  };

  const handleReportClose = () => {
    setReportDialogOpen(false);
    setSelectedReason(null);
  };

  const handleReportSubmit = async () => {
    if (!selectedReason) {
      toast.error('Please select a reason for reporting');
      return;
    }

    try {
      setIsReporting(true);
      await feedService.reportFeed(feed._id, selectedReason);
      toast.success('Report submitted successfully');
      handleReportClose();
    } catch (error) {
      console.error('Failed to submit report:', error);
      toast.error('Failed to submit report');
    } finally {
      setIsReporting(false);
    }
  };

  const isJobFeed = feed.feedType?.name?.toLowerCase() === 'job';
  const jobDetails = isJobFeed ? ((feed.feedSpecificDetails || feed.feedSpecificData) as JobDetails | undefined) : undefined;

  const handleApply = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!jobDetails || jobDetails.isApplied) return;
    try {
      await feedService.applyForJob(jobDetails.feedId);
      toast.success('Application submitted successfully');
      dispatch(updateFeedStats(feed._id));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to apply');
    }
  };

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'public': return <Public fontSize="small" />;
      case 'private': return <Lock fontSize="small" />;
      case 'connections': return <Group fontSize="small" />;
      default: return <Public fontSize="small" />;
    }
  };

  const formatTime = (dateString: string) => {
    try {
      // seconds difference (now - target)
      const diff = moment().diff(moment(dateString), "seconds");

      // convert the actual output to user local time
      const m = moment(dateString).local();

      if (diff < 60 && diff >= 0) {
        return `${diff} secs ago`;
      }

      return m.format("DD-MM-YYYY hh:mm A");
    } catch {
      return "Unknown time";
    }
  };


  return (
    <Paper
      key={feed._id}
      elevation={0}
      onClick={(e: any) => {
        e.stopPropagation();
        navigate(`/talky?feedId=${feed._id}`);
      }}
      sx={{
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}
      onMouseEnter={handleMouseEnter}
      >
        {/* Post Header - LinkedIn Style */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{ position: 'relative', mr: 1.5, flexShrink: 0 }}>
            {feed.userId.isLookingForJob ? (
              <Box sx={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }} onClick={() => navigate('/profile/' + feed.userId._id)}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -3,
                    left: -3,
                    right: -3,
                    bottom: -3,
                    borderRadius: '50%',
                    background: '#0a66c2',
                    padding: '3px',
                  }}
                >
                  <Box sx={{ width: '100%', height: '100%', borderRadius: '50%', background: theme.palette.background.paper }} />
                </Box>
                <Avatar
                  src={feed.userId.profilePicture?.url}
                  sx={{ bgcolor: '#0a66c2', width: 48, height: 48, position: 'relative', zIndex: 1 }}
                >
                  {feed.userId.name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -6,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#0a66c2',
                    color: 'white',
                    fontSize: '8px',
                    fontWeight: 600,
                    padding: '1px 6px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    zIndex: 2,
                  }}
                >
                  #OpenToWork
                </Box>
              </Box>
            ) : (
              <Avatar
                src={feed.userId.profilePicture?.url}
                onClick={() => navigate('/profile/' + feed.userId._id)}
                sx={{ bgcolor: '#0a66c2', width: 48, height: 48, cursor: 'pointer' }}
              >
                {feed.userId.name?.charAt(0) || 'U'}
              </Avatar>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => navigate('/profile/' + feed.userId._id)}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, color: 'text.primary' }}>
              {feed.userId.name}
            </Typography>
            {feed.userId.designation && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }} noWrap>
                {feed.userId.designation}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                {formatTime(feed.updatedAt)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'inline' } }}>•</Typography>
              <Box sx={{ display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center' }}>
                {getVisibilityIcon(feed.visibility)}
              </Box>
              {(feed.feedType || feed.poll) && (
                <>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>•</Typography>
                  <Chip
                    label={
                      feed.poll
                        ? 'Poll'
                        : feed.feedType?.name.toLowerCase() === 'jobseeker'
                          ? 'Open to Work'
                          : feed.feedType?.name
                    }
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      bgcolor: feed.poll
                        ? '#fff3e0'
                        : feed.feedType?.name.toLowerCase() === 'job'
                          ? '#e8f5e9'
                          : '#e3f2fd',
                      color: feed.poll
                        ? '#e65100'
                        : feed.feedType?.name.toLowerCase() === 'job'
                          ? '#2e7d32'
                          : '#1565c0',
                      textTransform: 'capitalize',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </>
              )}
            </Box>
          </Box>

          {/* Follow/Actions - LinkedIn Style */}
          <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 }, alignItems: 'center', ml: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
            {isAdmin && (
              <IconButton onClick={handleDeleteClick} size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                <Delete fontSize="small" />
              </IconButton>
            )}
            {!isOwnFeed && !hideActions && (
              <>
                {feed.relationshipStatus && (
                  <>
                    {localIsFollowing || feed.relationshipStatus.isFollowing ? (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleFollow}
                        sx={{ fontSize: '0.8rem', textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                      >
                        Following
                      </Button>
                    ) : localRequestPending || feed.relationshipStatus.isRequestSent ? (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleCancelRequest}
                        sx={{ fontSize: '0.8rem', textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                      >
                        Pending
                      </Button>
                    ) : (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleFollow}
                        sx={{ fontSize: '0.8rem', textTransform: 'none', fontWeight: 600, color: '#0a66c2' }}
                      >
                        + Follow
                      </Button>
                    )}
                  </>
                )}
                <IconButton
                  size="small"
                  onClick={handleMenuClick}
                  sx={{ color: 'text.secondary' }}
                >
                  <MoreHoriz />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleMenuClose}
                  onClick={(e) => e.stopPropagation()}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 0.5,
                      minWidth: 150,
                      boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
                      borderRadius: 2,
                    }
                  }}
                >
                  <MenuItem onClick={handleReportClick}>
                    <ListItemIcon>
                      <Flag fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      Report
                    </ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>

        {/* Apply Button - LinkedIn Style */}
        {isJobFeed && jobDetails && !isOwnFeed && !hideActions && (
          <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mb: 1 }}>
            <Button
              variant="contained"
              size="small"
              fullWidth={false}
              startIcon={jobDetails.isApplied ? <CheckCircleOutlineTwoTone /> : <Send />}
              onClick={handleApply}
              disabled={jobDetails.isApplied}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 5,
                px: 2,
                fontSize: '0.8rem',
                whiteSpace: 'nowrap',
                width: { xs: '100%', sm: 'auto' },
                bgcolor: jobDetails.isApplied ? '#057642' : '#0a66c2',
                '&:hover': { bgcolor: jobDetails.isApplied ? '#057642' : '#004182' },
                '&.Mui-disabled': { bgcolor: '#057642', color: 'white' },
              }}
            >
              {jobDetails.isApplied ? 'Applied' : 'Easy Apply'}
            </Button>
          </Box>
        )}

        {/* Post Title */}
        {feed.title && (
          <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.95rem' }}>
            {feed.title}
          </Typography>
        )}
    {/* Poll */}
        {feed.poll && (
          <PollCard 
            poll={feed.poll} 
            feedId={feed._id}
            userHasVoted={feed.userHasVoted}
            userVoteIndex={feed.userVoteIndex}
            onVoteSuccess={handleMouseEnter}
          />
        )}

        {/* Post Content */}
        <ContentWithReadMore 
          content={feed.content} 
          feedId={feed._id} 
          expandedContent={expandedContent} 
          setExpandedContent={setExpandedContent} 
        />


        {/* Job Details Card - Show only for job feeds */}
        {feed.feedType?.name === 'job' && (feed.feedSpecificDetails || feed.feedSpecificData) && (
          <JobDetailsCard 
            jobDetails={(feed.feedSpecificDetails || feed.feedSpecificData) as JobDetails} 
            feedUserId={feed.userId._id}
            feed={feed}
          />
        )}

        {/* Job Seeker Card - Show only for jobSeeker feeds */}
        {feed.feedType?.name === 'jobSeeker' && (feed.feedSpecificDetails || feed.feedSpecificData) && (
          <JobSeekerCard 
            jobSeekerDetails={(feed.feedSpecificDetails || feed.feedSpecificData) as JobSeekerDetails} 
            feedUserId={feed.userId._id}
            feed={feed}
          />
        )}

        {/* Tags - LinkedIn Style */}
        {feed.tags && feed.tags.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {feed.tags.map((tag, index) => (
              <Typography
                key={index}
                component="span"
                sx={{
                  color: '#0a66c2',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  mr: 1,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                #{tag}
              </Typography>
            ))}
          </Box>
        )}
        {/* Media */}
        <FeedMedia 
          media={feed.media}
          onImageClick={onImageClick}
          onDocumentClick={onDocumentClick}
        />

    
        {/* Link Preview - LinkedIn Style */}
        {feed.linkPreview && (
          <Box
            sx={{
              mt: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': { bgcolor: theme.palette.action.hover },
            }}
          >
            <Box sx={{ p: 1.5, bgcolor: theme.palette.mode === 'light' ? '#f3f2ef' : 'grey.900' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25, lineHeight: 1.3 }}>
                {feed.linkPreview.title}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                {feed.linkPreview.description}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                {feed.linkPreview.url}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>

      {/* Post Actions */}
      {!hideActions && (
        <>
          <FeedActions feed={feed} onCommentClick={handleCommentToggle} />
          
          {/* Comments Section */}
          <CommentList
            feedId={feed._id}
            showComments={showComments}
          />
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Post
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this post? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Post Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={handleReportClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Select your reporting reason
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {REPORT_REASONS.map((reason) => (
              <Chip
                key={reason}
                label={reason}
                onClick={() => setSelectedReason(reason)}
                color={selectedReason === reason ? 'primary' : 'default'}
                variant={selectedReason === reason ? 'filled' : 'outlined'}
                sx={{
                  borderRadius: '16px',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: selectedReason === reason ? 'primary.dark' : 'action.hover',
                  },
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleReportClose} color="inherit" disabled={isReporting}>
            Cancel
          </Button>
          <Button 
            onClick={handleReportSubmit} 
            color="primary" 
            variant="contained" 
            disabled={!selectedReason || isReporting}
            sx={{ borderRadius: '16px', px: 3 }}
          >
            {isReporting ? 'Reporting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
